import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * ThemeContext
 * ------------
 * Tenant-scoped theming. Loads `/api/settings/theme` on auth, then injects the
 * resolved colors into CSS custom properties on <html>. Components keep using
 * `var(--gold)`, `var(--sidebar)`, `var(--bg)`, `var(--accent-teal)` and pick
 * up the tenant's choice without per-component refactors.
 *
 * Sidebar mode:
 *   - "fixed"     → sidebar always expanded (w-64) and locked open
 *   - "auto-hide" → YouTube-style hover-expand rail (default)
 */

export const DEFAULT_THEME = {
  primary: "#E84672",
  sidebar: "#E84672",
  bg: "#7DDDDD",
  accent: "#2DD4D4",
  text: "#1E2A4A",
  sidebar_mode: "auto-hide",
};

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  saveTheme: async () => {},
  resetTheme: async () => {},
  loading: false,
});

export const useTheme = () => useContext(ThemeContext);

// hex (#rrggbb) → rgba string
function hexToRgba(hex, alpha) {
  if (!hex || hex.length !== 7) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// shade a hex color by a percentage (-100 = black, +100 = white).
function shade(hex, percent) {
  if (!hex || hex.length !== 7) return hex;
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;
  r = Math.round((t - r) * p) + r;
  g = Math.round((t - g) * p) + g;
  b = Math.round((t - b) * p) + b;
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function applyTheme(theme) {
  const root = document.documentElement;
  const set = (k, v) => root.style.setProperty(k, v);

  set("--gold", theme.primary);
  set("--gold-soft", shade(theme.primary, 15));
  set("--gold-hover", shade(theme.primary, -12));
  set("--gold-tint", hexToRgba(theme.primary, 0.08));
  set("--gold-tint-strong", hexToRgba(theme.primary, 0.18));

  set("--sidebar", theme.sidebar);
  set("--sidebar-soft", shade(theme.sidebar, -8));

  set("--bg", theme.bg);
  set("--bg-alt", shade(theme.bg, -6));

  set("--accent-teal", theme.accent);
  set("--accent-teal-soft", shade(theme.accent, 15));
  set("--accent-teal-tint", hexToRgba(theme.accent, 0.10));

  set("--text", theme.text);
  set("--text-strong", shade(theme.text, -25));

  // Sidebar mode is read by Layout.jsx via data-attribute on <html>.
  root.setAttribute("data-sidebar-mode", theme.sidebar_mode || "auto-hide");
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(false);

  // Load tenant theme whenever the user logs in (or changes).
  useEffect(() => {
    let alive = true;
    if (!user) {
      // No auth → reset to defaults so the login page stays branded.
      applyTheme(DEFAULT_THEME);
      setTheme(DEFAULT_THEME);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/settings/theme");
        if (!alive) return;
        const merged = { ...DEFAULT_THEME, ...r.data };
        applyTheme(merged);
        setTheme(merged);
      } catch {
        if (!alive) return;
        applyTheme(DEFAULT_THEME);
        setTheme(DEFAULT_THEME);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  const saveTheme = useCallback(async (patch) => {
    const r = await api.put("/settings/theme", patch);
    const merged = { ...DEFAULT_THEME, ...r.data };
    applyTheme(merged);
    setTheme(merged);
    return merged;
  }, []);

  const resetTheme = useCallback(async () => {
    const r = await api.post("/settings/theme/reset");
    const merged = { ...DEFAULT_THEME, ...r.data };
    applyTheme(merged);
    setTheme(merged);
    return merged;
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, saveTheme, resetTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
}
